import produce from 'immer';
import JWT from 'jsonwebtoken';
import queryString from 'querystringify';
import { registerReducer } from '../../../StateSetup/RootReducer';
import { APP_CONSTANTS, LOCALE } from '../../../utils/AppConstants';

const BFF_BASE_URL = window.__ENV__.BFF_URL;
const OK = 200;

// Action names
const ACTION_NAME_PREFIX = 'action/authenticate';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Authenticate the user either by getting the existing JWT from session storage or taking the token provided in the
 * URL and exchanging it for a JWT with the BFF.
 * @param {object} logger
 */
export const Authenticate = () => {
    return async (dispatch, getState, { logger }) => {
        const token = queryString.parse(window.location.search).token;
        const existingJwt = sessionStorage.getItem(APP_CONSTANTS.MPROFILE_JWT);
        const existingSessionCorrelationId = sessionStorage.getItem(APP_CONSTANTS.MPROFILE_SESSION_CORRELATION_ID);
        const transactionId = logger.generateGuid();

        if (!token) {
            // Is there already a JWT in this session?
            if (existingJwt) {
                const decodedToken = JWT.decode(existingJwt);

                logger.info(
                    `${ACTION_NAME_PREFIX}: No token found in URL but an existing session was found in sessionStorage.`,
                    { correlation: { transactionId } }
                );

                return dispatch({
                    type: RESPONSE_ACTION_NAME,
                    jwt: existingJwt,
                    language: decodedToken.Identity.language.toLowerCase().startsWith('fr') ? LOCALE.FR : LOCALE.EN,
                    isCsr: decodedToken.Identity.role.toLowerCase() === 'csr',
                    sessionCorrelationId: existingSessionCorrelationId,
                    isCiamSession: decodedToken.hasOwnProperty('CIAM'),
                    isSteppedUp: decodedToken?.CIAM?.amr?.includes('otp'),
                    success: true,
                    group: decodedToken.Identity.group,
                    ciamUuid: decodedToken.CIAM?.uuid,
                    entityId: decodedToken.SSO?.entity_id,
                });
            }
            // If no token was passed and there is no existing token no need to make service call... just error.
            logger.error(`${ACTION_NAME_PREFIX}: No token found in URL and there is no existing session.`, {
                correlation: { transactionId },
            });
            return dispatch({
                type: ERROR_ACTION_NAME,
            });
        }

        // Call BFF to login the user and get back a JWT
        logger.info(`${ACTION_NAME_PREFIX}: Authenticating with BFF.`, { correlation: { transactionId } });
        const response = await fetch(`${BFF_BASE_URL}/transfer/v1/in`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'mli-correlation-id': transactionId,
                'mli-channel': 'web',
            },
            body: JSON.stringify({
                'gb-transfer-token': token,
            }),
        });
        const responseBody = await response.json();

        if (response.status === OK) {
            const jwt = responseBody.tokens['gb-authorization'];
            const sessionCorrelationId = responseBody['mli-session-correlation-id'];
            const decodedToken = JWT.decode(jwt);

            // Calculating clock correction for session expiration management
            const serverTimestamp = responseBody['gb-server-timestamp'];
            sessionStorage.setItem(APP_CONSTANTS.MPROFILE_SESSION_OFFSET, Date.now() - serverTimestamp);

            logger.info(`${ACTION_NAME_PREFIX}: Authenticated successfully.`, { correlation: { transactionId } });

            return dispatch({
                type: RESPONSE_ACTION_NAME,
                language: decodedToken.Identity.language.toLowerCase().startsWith('fr') ? LOCALE.FR : LOCALE.EN,
                isCsr: decodedToken.Identity.role.toLowerCase() === 'csr',
                isCiamSession: decodedToken.hasOwnProperty('CIAM'),
                success: true,
                isSteppedUp: decodedToken?.CIAM?.amr?.includes('otp'),
                group: decodedToken.Identity.group,
                ciamUuid: decodedToken.CIAM?.uuid,
                entityId: decodedToken.SSO?.entity_id,
                jwt,
                sessionCorrelationId,
            });
        }
        logger.error(
            `${ACTION_NAME_PREFIX}: Authenticated failed. Code: ${responseBody.code} Reason: ${responseBody.message}`,
            { correlation: { transactionId } }
        );

        return dispatch({
            type: ERROR_ACTION_NAME,
        });
    };
};

/**
 * Handles a successful response when a user has been authenticated
 * @param {object} state
 * @param {object} action
 */
export const responseReducer = produce((state, action) => {
    state.Authentication.isAuthenticated = true;
    state.Authentication.jwt = action.jwt;
    state.Authentication.language = action.language;
    state.Authentication.isCsr = action.isCsr;
    state.Authentication.sessionCorrelationId = action.sessionCorrelationId;
    state.Authentication.isCiamSession = action.isCiamSession;
    state.Authentication.isSteppedUp = action.isSteppedUp;
    state.Authentication.group = action.group;
    state.Authentication.ciamUuid = action.ciamUuid;
    state.Authentication.entityId = action.entityId;
});

/**
 * Handles an error scenario where the user is not authenticated
 * @param {object} state
 * @param {object} action
 */
export const errorReducer = produce((state) => {
    state.Authentication.hasError = true;
});

// Register Reducers with Redux root reducer
registerReducer(RESPONSE_ACTION_NAME, responseReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
